import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import GrainTexture from '@/components/GrainTexture';
import GeoPattern from '@/components/GeoPattern';
import MaatwerkOpleidingDialog from '@/components/MaatwerkOpleidingDialog';
import { SEO } from '@/components/SEO';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import OptimizedImage from '@/components/OptimizedImage';
import agendaSfeerbeeld from '@/assets/agenda-sfeerbeeld.webp';

const Agenda = () => {
  const [maatwerkDialogOpen, setMaatwerkDialogOpen] = useState(false);

  return (
    <>
      <SEO 
        title="Agenda 2026 | leergang & themadagen"
        description="Zakelijke agenda 2026 op deze site: leergang en themadag. Voor familieopstellingen en de volledige agenda met inschrijven: systemique.nl/agenda."
        canonical="https://organisatieopstellingen.com/agenda"
        ogImage="https://organisatieopstellingen.com/agenda-social.png"
        ogDescription="Bekijk de nieuwe data voor leergangen en themadagen. Reserveer je plek."
        ogType="website"
      />
      
      {/* Agenda Overzicht Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "Agenda Organisatieopstellingen",
            "description": "Overzicht van komende trainingen en evenementen.",
            "url": "https://organisatieopstellingen.com/agenda",
            "mainEntity": [
              {
                "@type": "EducationEvent",
                "name": "Leergang systemisch leiderschap | Duiken in de Onderstroom",
                "description": "3-daagse leergang systemisch leiderschap voor leiders en adviseurs. 29 en 30 oktober + 27 november 2026, Amsterdam.",
                "startDate": "2026-10-29T09:00:00+02:00",
                "endDate": "2026-11-27T17:00:00+01:00",
                "eventStatus": "https://schema.org/EventScheduled",
                "image": "https://organisatieopstellingen.com/OG-image.jpg",
                "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
                "location": {
                  "@type": "Place",
                  "name": "Stadsoase naar Zee",
                  "url": "https://www.stadsoasenaarzee.com",
                  "address": {
                    "@type": "PostalAddress",
                    "streetAddress": "Bert Haanstrakade 102",
                    "addressLocality": "Amsterdam",
                    "postalCode": "1087 DN",
                    "addressRegion": "Noord-Holland",
                    "addressCountry": "NL"
                  }
                },
                "organizer": {
                  "@type": "Organization",
                  "name": "Organisatieopstellingen",
                  "url": "https://organisatieopstellingen.com"
                },
                "url": "https://organisatieopstellingen.com/leergang-onderstroom"
              },
              {
                "@type": "EducationEvent",
                "name": "Themadag: Burn-out in organisaties",
                "description": "Themadag voor HR-adviseurs en managers.",
                "startDate": "2026-10-22T13:00:00+02:00",
                "endDate": "2026-10-22T17:00:00+02:00",
                "eventStatus": "https://schema.org/EventScheduled",
                "image": "https://organisatieopstellingen.com/OG-image.jpg",
                "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
                "location": {
                  "@type": "Place",
                  "name": "Stadsoase naar Zee",
                  "url": "https://www.stadsoasenaarzee.com",
                  "address": {
                    "@type": "PostalAddress",
                    "streetAddress": "Bert Haanstrakade 102",
                    "addressLocality": "Amsterdam",
                    "postalCode": "1087 DN",
                    "addressRegion": "Noord-Holland",
                    "addressCountry": "NL"
                  }
                },
                "organizer": {
                  "@type": "Organization",
                  "name": "Organisatieopstellingen",
                  "url": "https://organisatieopstellingen.com"
                },
                "url": "https://organisatieopstellingen.com/themadag"
              }
            ]
          })
        }}
      />
      
      {/* Custom Luxury Header */}
      <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center justify-center px-6 overflow-hidden bg-[#854C73]">
        <div className="absolute inset-0 z-0">
          <GrainTexture opacity={0.2} />
          <div className="absolute top-0 right-0 w-[300px] h-[300px] md:w-[500px] md:h-[500px] lg:w-[600px] lg:h-[600px] border border-[#F9EBEF]/10 rounded-full translate-x-1/2 -translate-y-1/2 opacity-30"></div>
        </div>
        
        <div className="container mx-auto relative z-10 text-center">
          <Button variant="ghost" asChild className="mb-8 hover:bg-white/20 text-white hover:text-white border border-white/30 mx-auto flex w-fit px-6 py-2 h-auto cursor-pointer">
            <Link to="/">
              <ArrowLeft className="mr-2 w-4 h-4" /> Terug naar home
            </Link>
          </Button>
          
          <h1 className="font-serif text-3xl md:text-5xl text-[#F9EBEF] mb-6">
            De <span className="italic font-light font-serif">agenda 2026</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-[#F9EBEF]/80 font-serif italic max-w-2xl mx-auto">
            Leergang &amp; themadag en zakelijke events voor teams en HR
          </p>
        </div>
      </section>

      {/* Sfeerbeeld tussen hero en agenda-inhoud */}
      <section className="px-4 sm:px-6 pt-10 sm:pt-14 bg-white">
        <div className="max-w-2xl mx-auto">
          <div className="group rounded-2xl overflow-hidden shadow-md">
            <div className="aspect-[21/9] overflow-hidden">
              <OptimizedImage
                src={agendaSfeerbeeld}
                alt="Sfeerbeeld bij Systemique, leergangen en organisatieopstellingen"
                className="w-full h-full object-cover object-[center_25%] transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                width={1067}
                height={1600}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Breadcrumbs */}
      <section className="bg-white py-4 px-6">
        <div className="container mx-auto max-w-6xl">
          <Breadcrumbs className="text-[#854C73]" />
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-24 px-6 relative bg-white">
        <GrainTexture opacity={0.1} />
        
        <div className="container mx-auto max-w-5xl relative z-10">
          <div className="space-y-6">
            
            {/* Leergang Duiken in de Onderstroom Card */}
            <Link to="/leergang-onderstroom" className="block group">
              <div className="relative rounded-[2.5rem] overflow-hidden bg-[#6b4912] p-8 shadow-xl hover:shadow-2xl transition-all duration-300 text-white">
                <div className="absolute inset-0 opacity-30">
                  <GeoPattern />
                </div>
                
                <div className="relative z-10">
                  <div className="flex flex-wrap gap-2 mb-6">
                    <Badge className="border border-white/30 text-white bg-transparent">
                      Systemisch leiderschap
                    </Badge>
                    <Badge className="bg-[#F9EBEF] text-[#6b4912] border-0">
                      Najaar 2026
                    </Badge>
                  </div>
                  
                  <h3 
                    className="font-serif text-4xl leading-tight mb-4 group-hover:underline"
                    style={{ fontFamily: "'Noto Serif Display', serif", fontWeight: 700 }}
                  >
                    Leergang systemisch{' '}
                    <span className="italic font-serif text-[#FAEBEE] font-light">leiderschap</span>
                  </h3>
                  
                  <p className="text-white/90 text-base leading-relaxed font-light mb-6">
                    Duiken in de Onderstroom: 29 en 30 oktober · 27 november 2026, Amsterdam.
                    Compacte 3-daagse leergang voor leiders en adviseurs.
                  </p>
                  
                  <div className="flex items-center text-white font-medium text-sm">
                    <span>Bekijk leergang en data</span>
                    <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            </Link>

            {/* Themadag Card */}
            <Link to="/themadag" className="block group">
              <div className="relative rounded-[2.5rem] overflow-hidden bg-[#854C73] p-8 shadow-xl hover:shadow-2xl transition-all duration-300 text-white">
                <div className="absolute inset-0 opacity-30">
                  <GeoPattern />
                </div>
                
                <div className="relative z-10">
                  <div className="flex flex-wrap gap-2 mb-6">
                    <Badge className="border border-white/30 text-white bg-transparent">
                      Evenement
                    </Badge>
                    <Badge className="bg-[#F9EBEF] text-[#854C73] border-0">
                      22 oktober 2026
                    </Badge>
                  </div>
                  
                  <h3 
                    className="font-serif text-4xl leading-tight mb-4 group-hover:underline"
                    style={{ fontFamily: "'Noto Serif Display', serif", fontWeight: 700 }}
                  >
                    Themadag:{' '}
                    <span className="italic font-serif text-[#FAEBEE] font-light">Burn-out</span>
                  </h3>
                  
                  <p className="text-white/90 text-base leading-relaxed font-light mb-6">
                    We duiken een dag dieper in de thema's die in organisaties spelen als het gaat om burn-out. Waar is de burn-out in de organisatiecontext mee verbonden?
                  </p>
                  
                  <div className="flex items-center text-white font-medium text-sm">
                    <span>Themadag burn-out, 22 okt, Amsterdam</span>
                    <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            </Link>

            {/* Maatwerk Blok */}
            <div className="bg-white rounded-3xl shadow-lg border border-[#854C73]/10 p-8">
              <h3 
                className="font-serif text-2xl text-[#854C73] mb-4"
                style={{ fontFamily: "'Noto Serif Display', serif", fontWeight: 700 }}
              >
                Maatwerk voor <span className="italic font-serif font-light">jouw organisatie?</span>
              </h3>
              
              <p className="text-[#6b4912] text-sm leading-relaxed font-light mb-6">
                We organiseren ook op maat gemaakte opstellingen en trainingen voor teams en organisaties.
              </p>
              
              <Button
                onClick={() => setMaatwerkDialogOpen(true)}
                className="w-full bg-[#854C73] hover:bg-[#590D0D] text-white rounded-full"
              >
                Neem contact op
              </Button>
            </div>
          </div>
        </div>
      </section>
      
      <MaatwerkOpleidingDialog open={maatwerkDialogOpen} onOpenChange={setMaatwerkDialogOpen} />
    </>
  );
};

export default Agenda;
